"use client"

import { useCallback, useEffect, useState } from "react"
import { useRouter } from "next/navigation"
import {
  getToken,
  setToken as storeToken,
  clearToken,
  login as apiLogin,
  register as apiRegister,
} from "@/lib/api"
import { setSentryUser, clearSentryUser } from "@/lib/sentry"

interface UseAuthReturn {
  isAuthenticated: boolean
  userId: string | null
  email: string | null
  isLoading: boolean
  login: (email: string, password: string) => Promise<void>
  register: (email: string, password: string) => Promise<void>
  logout: () => void
  requireAuth: () => boolean
}

/** Decode the payload of a JWT (no verification — that's the server's job). */
function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const base64 = token.split(".")[1]
    if (!base64) return null
    const json = atob(base64.replace(/-/g, "+").replace(/_/g, "/"))
    return JSON.parse(json)
  } catch {
    return null
  }
}

export function useAuth(): UseAuthReturn {
  const router = useRouter()

  // Start as loading — avoids hydration mismatch between static HTML and client
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [userId, setUserId] = useState<string | null>(null)
  const [email, setEmail] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  // Read token from localStorage after mount (client-only)
  useEffect(() => {
    const token = getToken()
    if (token) {
      const payload = decodeJwtPayload(token)
      setIsAuthenticated(true)
      const uid = (payload?.user_id as string) ?? (payload?.sub as string) ?? null
      const em = (payload?.email as string) ?? null
      setUserId(uid)
      setEmail(em)
      if (uid) setSentryUser(uid, em ?? undefined)
    }
    setIsLoading(false)
  }, [])

  const login = useCallback(async (email: string, password: string) => {
    const data = await apiLogin(email, password)
    storeToken(data.token)
    const payload = decodeJwtPayload(data.token)
    setIsAuthenticated(true)
    setUserId(data.user_id)
    setEmail((payload?.email as string) ?? email)
    setSentryUser(data.user_id, (payload?.email as string) ?? email)
  }, [])

  const register = useCallback(async (email: string, password: string) => {
    const data = await apiRegister(email, password)
    storeToken(data.token)
    const payload = decodeJwtPayload(data.token)
    setIsAuthenticated(true)
    setUserId(data.user_id)
    setEmail((payload?.email as string) ?? email)
    setSentryUser(data.user_id, (payload?.email as string) ?? email)
  }, [])

  const logout = useCallback(() => {
    clearToken()
    clearSentryUser()
    setIsAuthenticated(false)
    setUserId(null)
    setEmail(null)
    router.push("/")
  }, [router])

  /** Returns true if authenticated, otherwise redirects to /login and returns false. */
  const requireAuth = useCallback((): boolean => {
    if (!getToken()) {
      router.push("/login")
      return false
    }
    return true
  }, [router])

  return {
    isAuthenticated,
    userId,
    email,
    isLoading,
    login,
    register,
    logout,
    requireAuth,
  }
}
